/**
 * Centralized API service.
 *
 * In development the CRA proxy (package.json "proxy") forwards requests to
 * localhost:5000, so a bare path like '/auth/forgot-password' works.
 * In production REACT_APP_API_URL must be set to the full backend origin.
 *
 * Every function returns the parsed JSON body and throws on non-2xx
 * responses, providing a single consistent error shape:
 *   { message: string }
 */

const BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/$/, '') // strip trailing slash
  : ''; // empty string → relative paths (dev proxy works)

// ─── Generic request helper ─────────────────────────────────────────────────
async function request(method, path, body) {
  const url = `${BASE_URL}${path}`;

  const options = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };

  if (body !== undefined) {
    options.body = JSON.stringify(body);
  }

  const res = await fetch(url, options);
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const err = new Error(data.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

// ─── Auth endpoints ──────────────────────────────────────────────────────────

/**
 * POST /auth/forgot-password
 * @param {string} identifier  email or phone number
 */
export async function forgotPassword(identifier) {
  return request('POST', '/auth/forgot-password', { identifier });
}

/**
 * GET /auth/validate-reset-token?token=...
 * @param {string} token  raw (un-hashed) reset token from URL
 */
export async function validateResetToken(token) {
  return request('GET', `/auth/validate-reset-token?token=${encodeURIComponent(token)}`);
}

/**
 * POST /auth/reset-password
 * @param {string} token        raw reset token from URL
 * @param {string} newPassword  the new plaintext password chosen by the user
 */
export async function resetPassword(token, newPassword) {
  return request('POST', '/auth/reset-password', { token, newPassword });
}

// ─── Contact endpoint ────────────────────────────────────────────────────────

/**
 * POST /contact
 * @param {{ name: string, email: string, subject: string, message: string }} data
 */
export async function submitContact(data) {
  return request('POST', '/contact', data);
}
